import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'styled-components';
import SVG from 'react-inlinesvg';
import colors from 'shared-assets/lib/colors';
import { mix } from 'polished';

const iconFillColors = {
  success: mix(0.3, '#000', colors.success),
  error: mix(0.4, '#000', colors.error),
  warning: mix(0.3, '#000', colors.warning),
  info: mix(0.3, '#000', colors.info),
};

const FlashPanelContainer = styled.div`
  border-color: transparent;
  color: #fff;
  background: ${props => colors[props.displayType]};

  max-width: 350px;
  position: absolute;
  padding: 0.35em 0.5em 0.45em;
  border-radius: 3px;
  border: 0;
  font-weight: 600;

  ${props => {
    if (props.autoPosition) {
      return `
        top: 1rem;
        left: 50%;
        transform: translate(-50%,0%);
      `;
    }
    return null;
  }} box-shadow: 0 1px 8px rgba(0,0,0,.1);
  z-index: 100;
  margin-bottom: 1em;
  transition: all ease-in-out 0.2s;
  a {
    color: #fff;
  }
`;

// The "&&" syntax leverages a hack in styled components to increase specificity
// per https://github.com/styled-components/styled-components/issues/1253#issuecomment-337871693
// (Each "&" gets replaced with the generated class name, so if the class name were "abc" then
// "&&&" would generate ".abc.abc.abc")
const CloseLink = styled.a`
  && {
    float: right;
    text-decoration: none;
    font-weight: 700;
    margin-right: 0;
    fill: ${props => iconFillColors[props.type]};
    width: 14px;
    height: 14px;
    cursor: pointer;
  }
  svg {
    width: 0.625rem;
    height: 0.625rem;
  }
`;

const TextWithIcon = styled.div`
  position: relative;
  display: flex;
  padding-left: 0;
  padding-right: 0.5em;
`;

const Icon = styled.i`
  && {
    min-width: 16px;
    min-height: 16px;
    font-size: 12px;
    font-weight: 400;
    margin-right: 0.5em;
    margin-top: 0.25em;

    svg path {
      fill: ${props => iconFillColors[props.type]};
    }
  }
`;

export const displayTypes = {
  error: 'error',
  info: 'info',
  success: 'success',
  warning: 'warning',
};

export default class FlashPanel extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    displayType: PropTypes.oneOf(Object.values(displayTypes)),
    hasClose: PropTypes.bool,
    fadeOut: PropTypes.bool,
    fadeOutMs: PropTypes.number,
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node),
    ]).isRequired,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    style: PropTypes.object,
    autoPosition: PropTypes.bool,
  };

  static defaultProps = {
    className: null,
    fadeOutMs: 7000,
    hasClose: false,
    isOpen: true,
    displayType: displayTypes.success,
    fadeOut: true,
    onClose: () => {},
    autoPosition: true,
    style: null,
  };

  fadeTimeout = null;
  afterFadeTimeout = null;

  state = {
    isFadingIn: false,
    isFadingOut: false,
    isOpen: false,
  };

  componentDidMount = () => {
    if (this.props.isOpen) {
      this.open();
    }
  };

  componentDidUpdate = oldProps => {
    if (this.props.isOpen && !oldProps.isOpen) {
      this.open();
    }
    if (!this.props.isOpen && oldProps.isOpen) {
      this.close();
    }
  };

  close = () => {
    this.clearTimeouts();
    this.setState({
      isOpen: false,
    });
    this.props.onClose();
  };

  open = () => {
    this.clearTimeouts();
    this.setState({
      isOpen: true,
      isFadingIn: false,
      isFadingOut: false,
    });

    if (this.props.fadeOut) {
      this.setState({
        isFadingIn: true,
      });
      this.fadeTimeout = setTimeout(this.startFadeOut, this.props.fadeOutMs);
    }
  };

  startFadeOut = () => {
    this.setState({
      isFadingOut: true,
    });
    this.afterFadeTimeout = setTimeout(this.close, 200);
  };

  clearTimeouts = () => {
    if (this.fadeTimeout) {
      clearTimeout(this.fadeTimeout);
    }
    if (this.afterFadeTimeout) {
      clearTimeout(this.afterFadeTimeout);
    }
  };

  componentWillUnmount = () => {
    this.clearTimeouts();
  };

  render = () => {
    if (!this.state.isOpen) {
      return null;
    }
    return (
      <FlashPanelContainer
        displayType={this.props.displayType}
        autoPosition={this.props.autoPosition}
        className={classNames([
          {
            'animate-fadein': this.state.isFadingIn,
            'animate-fadeout': this.state.isFadingOut,
          },
          this.props.className,
        ])}
        style={this.props.style}
      >
        {this.props.hasClose && (
          <CloseLink type={this.props.displayType} onClick={this.close}>
            <SVG
              cacheGetRequests
              src={require('shared-assets/assets/icons/close.svg')}
            />
          </CloseLink>
        )}
        <TextWithIcon>
          <Icon type={this.props.displayType}>
            {this.props.displayType === displayTypes.success && (
              <SVG
                cacheGetRequests
                src={require('shared-assets/assets/icons/check-mark-alt.svg')}
              />
            )}
            {(this.props.displayType === displayTypes.error ||
              this.props.displayType === displayTypes.warning) && (
              <SVG
                cacheGetRequests
                src={require('shared-assets/assets/icons/attention-alt.svg')}
              />
            )}
            {this.props.displayType === displayTypes.info && (
              <SVG
                cacheGetRequests
                src={require('shared-assets/assets/icons/info-alt.svg')}
              />
            )}
          </Icon>
          <div>
            <React.Fragment>{this.props.children}</React.Fragment>
          </div>
        </TextWithIcon>
      </FlashPanelContainer>
    );
  };
}
